import React, { useState, useEffect } from "react";
import api from "../api/axios";
import axios from "axios";

const SuperAdmin = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState(null);
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState("");
  const [tableData, setTableData] = useState([]);
  const [queryInput, setQueryInput] = useState("");
  const [queryResult, setQueryResult] = useState([]);
  const [loading, setLoading] = useState(false);

  // Clear all tokens when component mounts - force fresh login
  useEffect(() => {
    console.log("SuperAdmin page loaded - clearing all tokens");

    // Clear superadmin token
    localStorage.removeItem("superadmin_token");

    // Clear regular admin tokens
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");

    // Clear any cookies
    document.cookie =
      "superadmin_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie =
      "access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie =
      "refresh_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

    // Clear sessionStorage
    sessionStorage.clear();

    // Reset state to ensure clean login
    setToken(null);
    setIsLoggedIn(false);
    setTables([]);
    setTableData([]);
    setQueryResult([]);
    setSelectedTable("");

    console.log("All tokens cleared - user must login");
  }, []); // Empty dependency array means this only runs once when component mounts

  const isValidJWT = (token) => {
    try {
      return token && token.split(".").length === 3;
    } catch {
      return false;
    }
  };

  const superAdminAPI = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:3001/api",
    headers: {
      "Content-Type": "application/json",
    },
  });

  const handleLogin = () => {
    const username = prompt("Enter Super Admin Username:");
    const password = prompt("Enter Super Admin Password:");

    if (!username || !password) {
      alert("Username and password are required!");
      return;
    }

    login(username, password);
  };

  const login = async (username, password) => {
    try {
      setLoading(true);
      console.log("Logging in with:", { username, password }); // DEBUG

      const response = await superAdminAPI.post("/superadmin/login", {
        username,
        password,
      });

      console.log("Login response:", response.data); // DEBUG

      const { access_token } = response.data;

      // Set ke localStorage
      localStorage.setItem("superadmin_token", access_token);

      // Set ke state
      setToken(access_token);
      setIsLoggedIn(true);

      console.log("Token saved and login successful"); // DEBUG

      alert("Login successful!");

      // Fetch tables after successful login
      setTimeout(() => {
        fetchTables(access_token);
      }, 500);
    } catch (error) {
      console.error("Login error:", error.response?.data || error);
      alert(
        "Login failed: " + (error.response?.data?.message || error.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("superadmin_token");
    setToken(null);
    setIsLoggedIn(false);
    setTables([]);
    setTableData([]);
    setQueryResult([]);
    setSelectedTable("");
    alert("Logged out successfully!");
  };

  const fetchTableData = async (tableName) => {
    try {
      setLoading(true);
      const response = await superAdminAPI.get(
        `/superadmin/tables/${tableName}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setTableData(response.data.data);
      setSelectedTable(tableName);
    } catch (error) {
      alert("Failed to fetch table data: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTables = async (tokenToUse = token) => {
    try {
      console.log("Fetching tables with token:", tokenToUse);

      const response = await superAdminAPI.get("/superadmin/tables", {
        headers: { Authorization: `Bearer ${tokenToUse}` },
      });
      setTables(response.data.data);
    } catch (error) {
      console.error(
        "Failed to fetch tables:",
        error.response?.data || error.message
      );
    }
  };

  const executeQuery = async () => {
    if (!queryInput.trim()) {
      alert("Please enter a query!");
      return;
    }

    try {
      setLoading(true);
      const response = await superAdminAPI.post(
        "/superadmin/query",
        {
          query: queryInput,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setQueryResult(response.data.data);
      alert("Query executed successfully!");
    } catch (error) {
      alert(
        "Query failed: " + (error.response?.data?.message || error.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const exportTableCSV = async (tableName) => {
    try {
      const response = await superAdminAPI.get(
        `/superadmin/tables/${tableName}/export`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${tableName}_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      alert("CSV exported successfully!");
    } catch (error) {
      alert("Export failed: " + error.message);
    }
  };

  const exportQueryCSV = async () => {
    if (!queryInput.trim()) {
      alert("Please enter a query!");
      return;
    }

    try {
      const response = await superAdminAPI.post(
        "/superadmin/query/export",
        {
          query: queryInput,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `query_export_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      alert("Query CSV exported successfully!");
    } catch (error) {
      alert("Export failed: " + error.message);
    }
  };

  const renderTable = (data) => {
    if (!data || data.length === 0) {
      return (
        <p className="text-gray-500 text-center py-4">No data available</p>
      );
    }

    const headers = Object.keys(data[0]);

    return (
      <div className="overflow-x-auto max-h-96 border border-gray-300 rounded-lg">
        <table className="min-w-full table-auto">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              {headers.map((header) => (
                <th
                  key={header}
                  className="border-b border-gray-200 px-4 py-2 text-left text-sm font-medium text-gray-700"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.map((row, index) => (
              <tr key={index} className="hover:bg-gray-50">
                {headers.map((header) => (
                  <td
                    key={header}
                    className="px-4 py-2 text-sm text-gray-900 border-b border-gray-100"
                  >
                    {String(row[header] || "")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md w-96 text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-6">
            Super Admin Panel
          </h1>
          <button
            onClick={handleLogin}
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 mb-4"
          >
            {loading ? "Logging in..." : "Login"}
          </button>
          <button
            onClick={() => (window.location.href = "/")}
            className="w-full bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-3 px-6 rounded-lg transition duration-200"
          >
            Kembali ke Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-gray-800">
              Super Admin Panel
            </h1>
            <button
              onClick={handleLogout}
              className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition duration-200"
            >
              Logout
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Skema relasi
          </h2>
          <div className="flex flex-wrap gap-2 mb-6">
            <a href="https://dbdiagram.io/d/6893c40cdd90d17865c9ced4">
              <img src="schema.svg" alt="Schema" />
            </a>
          </div>
        </div>
        {/* Tables Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Database Tables
          </h2>

          {/* Table Buttons */}
          <div className="flex flex-wrap gap-2 mb-6">
            {tables.map((table) => (
              <button
                key={table}
                onClick={() => fetchTableData(table)}
                className={`px-4 py-2 rounded-lg font-medium transition duration-200 ${
                  selectedTable === table
                    ? "bg-green-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                }`}
              >
                {table}
              </button>
            ))}
          </div>

          {/* Selected Table Data */}
          {selectedTable && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-800">
                  Table: {selectedTable}
                </h3>
                <button
                  onClick={() => exportTableCSV(selectedTable)}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white font-medium py-2 px-4 rounded-lg transition duration-200"
                >
                  Export CSV
                </button>
              </div>
              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="mt-2 text-gray-600">Loading...</p>
                </div>
              ) : (
                renderTable(tableData)
              )}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Custom SQL Query
          </h2>
          <p className="text-sm font-bold text-red-600 mb-4">
            PERINGATAN! Pastikan query yang Anda masukkan benar dan aman.
            <br />
            Query yang salah atau berbahaya dapat merusak database.
          </p>
          {/* Query Input */}
          <div className="mb-4">
            <textarea
              value={queryInput}
              onChange={(e) => setQueryInput(e.target.value)}
              placeholder="Enter your SQL query here..."
              className="w-full h-32 p-4 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Query Actions */}
          <div className="flex gap-3 mb-6">
            <button
              onClick={executeQuery}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2 px-4 rounded-lg transition duration-200"
            >
              {loading ? "Executing..." : "Execute Query"}
            </button>
            <button
              onClick={exportQueryCSV}
              className="bg-cyan-600 hover:bg-cyan-700 text-white font-medium py-2 px-4 rounded-lg transition duration-200"
            >
              Export Query as CSV
            </button>
          </div>

          {/* Query Results */}
          {queryResult.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-800 mb-4">
                Query Results:
              </h3>
              {renderTable(queryResult)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuperAdmin;
